import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import FormSteps from '../FormSteps';
import uploadCloudIcon from '../../../../../assets/svg/upload-cloud-02.svg';
import { PRESIGN_LINK } from '../../../../../graphql/PRESIGN_LINK';
import { EUploadTypes, formatFilename, uploadToS3 } from '../../../../Upload';

interface BrandLogoUploadStepProps {
    formik: any;
    goToNextStep: () => void;
    skipStep: () => void;
}

const BrandLogoUploadStep: React.FC<BrandLogoUploadStepProps> = ({
    formik,
    goToNextStep,
    skipStep
}) => {
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [preSignLink] = useMutation(PRESIGN_LINK);

    // Initialize preview with existing logo from database
    React.useEffect(() => {
        if (formik.values.brandLogo) {
            setLogoPreview(formik.values.brandLogo);
        }
    }, [formik.values.brandLogo]);

    const handleNext = () => {
        goToNextStep();
    };

    const handleSkip = () => {
        formik.setFieldValue('brandLogo', '');
        setLogoPreview(null);
        skipStep();
    };

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];
        setIsUploading(true);

        try {
            const uploadType = EUploadTypes.avatar;

            const signedLink = await preSignLink({
                variables: {
                    fileName: formatFilename(file.name),
                    fileType: file.type,
                    uploadType
                }
            });

            if (!signedLink.data?.preSignLink) {
                throw new Error('No presigned link returned from server');
            }

            const { signedRequest, url } = signedLink.data.preSignLink;

            // Upload to S3
            await uploadToS3(file, signedRequest);

            // Update formik field with the uploaded file URL
            formik.setFieldValue('brandLogo', url);

            // Set preview with the S3 URL
            setLogoPreview(url);

            toast.success('Logo uploaded successfully!');
        } catch (error: any) {
            console.error('Upload error details:', error);
            if (error?.graphQLErrors) {
                console.error('GraphQL errors:', error.graphQLErrors);
            }
            if (error?.networkError) {
                console.error('Network error:', error.networkError);
            }
            toast.error(`Failed to upload logo: ${error?.message || 'Unknown error'}`);
        } finally {
            setIsUploading(false);
        }
    }, [formik, preSignLink]);

    const onDropRejected = () => {
        toast.error('File is too large. Maximum size is 5MB.', {
            autoClose: false
        });
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: 'image/*',
        maxSize: 5000000, // 5MB
        multiple: false,
        noClick: false,
        noKeyboard: false,
        onDrop,
        onDropRejected
    });

    return (
        <FormSteps
            header="Upload your brand logo"
            description="Your brand logo helps identify your brand."
            nextButton={handleNext}
            skipButton={handleSkip}
        >
            <div className="field mb-6">
                <div
                    {...getRootProps()}
                    className="brand-logo-upload-field"
                    style={{
                        cursor: 'pointer',
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center'
                    }}
                >
                    <input {...getInputProps()} />
                    {isUploading ? (
                        <div className="upload-placeholder">
                            <progress className="progress is-small is-info" max="100">
                                Uploading...
                            </progress>
                            <p>Uploading...</p>
                        </div>
                    ) : logoPreview ? (
                        <div className="logo-preview">
                            <img
                                src={logoPreview}
                                alt="Brand logo preview"
                                style={{ maxWidth: '200px', maxHeight: '200px' }}
                            />
                            <p className="has-text-centered mt-2">
                                Click or drag to change logo
                            </p>
                        </div>
                    ) : (
                        <div className="upload-placeholder">
                            <div className="upload-icon">
                                <img
                                    src={uploadCloudIcon}
                                    alt="Upload"
                                />
                            </div>
                            <div className="upload-text-container">
                                <div className="upload-text">
                                    <span className="upload-link">Click To Upload</span> or drag and drop
                                </div>
                                <div className="upload-requirements">
                                    SVG, PNG, JPG or GIF (max. 5MB)
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </FormSteps>
    );
};

export default BrandLogoUploadStep;